/**
 * Recipe import by URL from embedded schema.org/Recipe metadata
 * as JSON-LD (`<script type="application/ld+json">`).
 *
 * DETERMINISTIC and AI-FREE: only reads what the web already publishes in
 * structured form (the standard consumed by Google/Pinterest). The backend acts as a proxy
 * that fetches the HTML (avoids CORS + guards SSRF); ALL schema.org → recipe-draft
 * mapping lives here, in pure logic testable without network or DOM.
 *
 * The draft is filled into the "Nueva receta" form so the user
 * can review/edit it before saving — a recipe is never persisted without confirmation.
 */
import { normalizeUnit } from "../shopping/units";

export interface ImportedIngredient {
  name: string;
  quantity: number | null;
  unit: string | null;
}

export interface ImportedNutrition {
  calories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
}

export interface ImportedRecipeDraft {
  name: string;
  servings: number | null;
  ingredients: ImportedIngredient[];
  steps: string[];
  tags: string[];
  notes: string | null;
  photoUrl: string | null;
  nutrition: ImportedNutrition;
}

type JsonObject = Record<string, unknown>;

const MAX_INGREDIENTS = 100;
const MAX_STEPS = 100;
const MAX_TAGS = 30;
const ROUND = 100;

/** Common Unicode fractions in recipes (½, ¾, ⅓…) to their decimal value. */
const VULGAR_FRACTIONS: Record<string, number> = {
  "¼": 0.25,
  "½": 0.5,
  "¾": 0.75,
  "⅓": 1 / 3,
  "⅔": 2 / 3,
  "⅕": 0.2,
  "⅖": 0.4,
  "⅗": 0.6,
  "⅘": 0.8,
  "⅙": 1 / 6,
  "⅚": 5 / 6,
  "⅛": 0.125,
  "⅜": 0.375,
  "⅝": 0.625,
  "⅞": 0.875,
};

function round2(value: number): number {
  return Math.round(value * ROUND) / ROUND;
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** First non-empty string from a value that may be a string, number, or array. */
function firstString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstString(item);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Extracts the raw content of each `<script type="application/ld+json">` block.
 * Regex (not DOM) so it can be tested in node/bun without jsdom; tolerant of attribute
 * order and whitespace.
 */
export function extractJsonLdBlocks(html: string): string[] {
  const blocks: string[] = [];
  const re =
    /<script\b[^>]*\btype\s*=\s*["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script\s*>/giu;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html)) !== null) {
    const raw = match[1];
    if (raw && raw.trim()) {
      blocks.push(raw.trim());
    }
  }
  return blocks;
}

/** Parses each block and flattens `@graph`/arrays into a list of object nodes. */
export function parseJsonLdNodes(blocks: string[]): JsonObject[] {
  const nodes: JsonObject[] = [];
  const push = (value: unknown): void => {
    if (Array.isArray(value)) {
      for (const item of value) {
        push(item);
      }
      return;
    }
    if (isObject(value)) {
      nodes.push(value);
      if (Array.isArray(value["@graph"])) {
        push(value["@graph"]);
      }
    }
  };
  for (const block of blocks) {
    try {
      push(JSON.parse(block));
    } catch {
      // Malformed JSON-LD block: ignored; it does not break the import.
    }
  }
  return nodes;
}

function typeMatchesRecipe(node: JsonObject): boolean {
  const type = node["@type"];
  if (typeof type === "string") {
    return type.toLowerCase() === "recipe";
  }
  if (Array.isArray(type)) {
    return type.some((t) => typeof t === "string" && t.toLowerCase() === "recipe");
  }
  return false;
}

/** Finds the first node whose `@type` is (or includes) "Recipe". */
export function findRecipeNode(nodes: JsonObject[]): JsonObject | null {
  return nodes.find(typeMatchesRecipe) ?? null;
}

/**
 * Quantity at the start of an ingredient text. Supports integers, decimals
 * (`,`/`.`), fractions (`1/2`), mixed numbers (`1 1/2`), Unicode fractions (`1½`), and
 * ranges (`2-3` → first value). Returns the quantity and the rest of the text.
 */
export function parseLeadingQuantity(text: string): { quantity: number | null; rest: string } {
  const trimmed = text.trim();

  // Integer/decimal (possibly followed by a Unicode fraction: "1 ½", "1½").
  const decimal = /^(\d+(?:[.,]\d+)?)\s*([¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])?/u.exec(trimmed);
  const leadingFraction = /^([¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/u.exec(trimmed);
  const asciiFraction = /^(\d+)\s+(\d+)\s*\/\s*(\d+)/u.exec(trimmed); // mixta "1 1/2"
  const simpleFraction = /^(\d+)\s*\/\s*(\d+)/u.exec(trimmed); // "1/2"

  if (asciiFraction) {
    const whole = Number(asciiFraction[1]);
    const num = Number(asciiFraction[2]);
    const den = Number(asciiFraction[3]);
    const value = den === 0 ? whole : whole + num / den;
    return { quantity: round2(value), rest: trimmed.slice(asciiFraction[0].length).trim() };
  }
  if (simpleFraction) {
    const num = Number(simpleFraction[1]);
    const den = Number(simpleFraction[2]);
    if (den !== 0) {
      return { quantity: round2(num / den), rest: trimmed.slice(simpleFraction[0].length).trim() };
    }
  }
  if (decimal) {
    let value = Number((decimal[1] ?? "").replace(",", "."));
    const vulgar = decimal[2];
    const vulgarValue = vulgar ? VULGAR_FRACTIONS[vulgar] : undefined;
    if (vulgarValue !== undefined) {
      value += vulgarValue;
    }
    if (Number.isFinite(value) && value > 0) {
      return { quantity: round2(value), rest: trimmed.slice(decimal[0].length).trim() };
    }
  }
  if (leadingFraction) {
    const key = leadingFraction[1];
    const value = key === undefined ? undefined : VULGAR_FRACTIONS[key];
    if (value !== undefined) {
      return { quantity: round2(value), rest: trimmed.slice(leadingFraction[0].length).trim() };
    }
  }
  return { quantity: null, rest: trimmed };
}

/**
 * Splits a free-form line ("200 g flour", "2 eggs", "Salt to taste") into
 * {quantity, unit, name}. The unit is only separated if the first token after
 * the quantity is a RECOGNIZED unit (family != "other"); otherwise it remains part
 * of the name so units are not invented.
 */
export function parseIngredientLine(line: string): ImportedIngredient {
  const cleaned = line.replaceAll(/\s+/gu, " ").trim();
  if (!cleaned) {
    return { name: "", quantity: null, unit: null };
  }
  const { quantity, rest } = parseLeadingQuantity(cleaned);
  if (quantity === null) {
    return { name: cleaned, quantity: null, unit: null };
  }

  const spaceIdx = rest.indexOf(" ");
  const firstToken = spaceIdx === -1 ? rest : rest.slice(0, spaceIdx);
  const remainder = spaceIdx === -1 ? "" : rest.slice(spaceIdx + 1).trim();
  const normalized = normalizeUnit(firstToken);
  const isKnownUnit = firstToken.length > 0 && normalized.family !== "other";

  if (isKnownUnit && remainder) {
    return { name: remainder, quantity, unit: firstToken.toLowerCase() };
  }
  return { name: rest, quantity, unit: null };
}

/** `recipeYield` → number of servings (first integer found). */
export function parseServings(value: unknown): number | null {
  const raw = firstString(value);
  if (!raw) {
    return null;
  }
  const match = /\d+/u.exec(raw);
  if (!match) {
    return null;
  }
  const parsed = Number.parseInt(match[0], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * `recipeInstructions` → list of steps. Supports a plain string (paragraphs), an
 * array of strings, an array of `HowToStep` ({text}), and `HowToSection` with
 * nested `itemListElement`.
 */
export function parseInstructions(value: unknown): string[] {
  const steps: string[] = [];
  const visit = (item: unknown): void => {
    if (steps.length >= MAX_STEPS) {
      return;
    }
    if (typeof item === "string") {
      for (const part of item.split(/\r?\n+/u)) {
        const trimmed = part.trim();
        if (trimmed) {
          steps.push(trimmed);
        }
      }
      return;
    }
    if (Array.isArray(item)) {
      for (const child of item) {
        visit(child);
      }
      return;
    }
    if (isObject(item)) {
      // HowToSection: its steps hang from itemListElement; its `name` is the
      // section title, NOT a step, so it is not emitted.
      if (item.itemListElement !== undefined) {
        visit(item.itemListElement);
        return;
      }
      const text = firstString(item.text) ?? firstString(item.name);
      if (text) {
        steps.push(text);
      }
    }
  };
  visit(value);
  return steps.slice(0, MAX_STEPS);
}

/** `keywords`/`recipeCategory`/`recipeCuisine` → normalized tags. */
export function parseKeywords(value: unknown): string[] {
  const raw: string[] = [];
  if (typeof value === "string") {
    raw.push(...value.split(","));
  } else if (Array.isArray(value)) {
    for (const item of value) {
      const s = firstString(item);
      if (s) {
        raw.push(s);
      }
    }
  }
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const item of raw) {
    const tag = item.trim();
    const key = tag.toLowerCase();
    if (tag && !seen.has(key)) {
      seen.add(key);
      tags.push(tag);
    }
  }
  return tags.slice(0, MAX_TAGS);
}

/** `image` → first URL (string, {url}, or an array of either). */
export function parseImageUrl(value: unknown): string | null {
  if (typeof value === "string") {
    return value.trim() || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const url = parseImageUrl(item);
      if (url) {
        return url;
      }
    }
    return null;
  }
  if (isObject(value)) {
    return firstString(value.url) ?? firstString(value.contentUrl);
  }
  return null;
}

/** First number in text such as "240 kcal" / "12 g" / 12. */
function firstNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? round2(value) : null;
  }
  if (typeof value === "string") {
    const match = /\d+(?:[.,]\d+)?/u.exec(value);
    if (match) {
      const parsed = Number(match[0].replace(",", "."));
      return Number.isFinite(parsed) ? round2(parsed) : null;
    }
  }
  return null;
}

/** `nutrition` (NutritionInformation) → macros per serving. */
export function parseNutrition(value: unknown): ImportedNutrition {
  const empty: ImportedNutrition = { calories: null, proteinG: null, carbsG: null, fatG: null };
  if (!isObject(value)) {
    return empty;
  }
  return {
    calories: firstNumber(value.calories),
    proteinG: firstNumber(value.proteinContent),
    carbsG: firstNumber(value.carbohydrateContent),
    fatG: firstNumber(value.fatContent),
  };
}

/** Maps a schema.org/Recipe node to a recipe draft. */
export function parseRecipeFromJsonLd(node: JsonObject): ImportedRecipeDraft {
  const ingredientsRaw = node.recipeIngredient ?? node.ingredients;
  const ingredients: ImportedIngredient[] = [];
  if (Array.isArray(ingredientsRaw)) {
    for (const item of ingredientsRaw) {
      const line = firstString(item);
      if (line) {
        ingredients.push(parseIngredientLine(line));
      }
      if (ingredients.length >= MAX_INGREDIENTS) {
        break;
      }
    }
  } else {
    const single = firstString(ingredientsRaw);
    if (single) {
      ingredients.push(parseIngredientLine(single));
    }
  }

  const tags = parseKeywords(node.keywords);
  if (tags.length === 0) {
    tags.push(...parseKeywords(node.recipeCategory));
  }

  return {
    name: firstString(node.name) ?? "",
    servings: parseServings(node.recipeYield ?? node.yield),
    ingredients,
    steps: parseInstructions(node.recipeInstructions),
    tags,
    notes: firstString(node.description),
    photoUrl: parseImageUrl(node.image),
    nutrition: parseNutrition(node.nutrition),
  };
}

/**
 * Entry point: recipe-page HTML → draft, or `null` if there is no
 * usable schema.org/Recipe node (no name and no ingredients).
 */
export function parseRecipeFromHtml(html: string): ImportedRecipeDraft | null {
  const nodes = parseJsonLdNodes(extractJsonLdBlocks(html));
  const recipeNode = findRecipeNode(nodes);
  if (!recipeNode) {
    return null;
  }
  const draft = parseRecipeFromJsonLd(recipeNode);
  if (!draft.name && draft.ingredients.length === 0) {
    return null;
  }
  return draft;
}
